import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface UseImageUploadOptions {
  bucket: 'avatars' | 'company-logos';
  prefix?: string;
  maxSizeMB?: number;
}

interface UseImageUploadReturn {
  previewUrl: string | null;
  uploadedUrl: string | null;
  isUploading: boolean;
  error: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileSelect: (file: File) => void;
  triggerFileInput: () => void;
  clearImage: () => void;
  resetError: () => void;
}

export function useImageUpload({
  bucket,
  prefix = '',
  maxSizeMB = 5,
}: UseImageUploadOptions): UseImageUploadReturn {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);

      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione um arquivo de imagem (JPG, PNG, WebP, GIF)');
        return;
      }

      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        setError(`A imagem deve ter no máximo ${maxSizeMB}MB`);
        return;
      }

      // Criar preview local imediatamente
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      setIsUploading(true);

      try {
        const fileExt = file.name.split('.').pop() || 'png';
        const fileName = `${prefix}${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        const filePath = fileName;

        const { data, error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true,
          });

        if (uploadError) {
          // Fallback: se o bucket não existir ou outro erro, guarda como base64 no localStorage
          console.warn('Upload para Supabase falhou, usando fallback local:', uploadError.message);
          const base64 = await fileToBase64(file);
          setUploadedUrl(base64);
          setIsUploading(false);
          return;
        }

        const { data: publicUrlData } = supabase.storage
          .from(bucket)
          .getPublicUrl(data.path);

        setUploadedUrl(publicUrlData.publicUrl);
      } catch (err) {
        console.warn('Erro no upload, usando fallback local:', err);
        const base64 = await fileToBase64(file);
        setUploadedUrl(base64);
      } finally {
        setIsUploading(false);
      }
    },
    [bucket, prefix, maxSizeMB],
  );

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const clearImage = useCallback(() => {
    setPreviewUrl(null);
    setUploadedUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    previewUrl,
    uploadedUrl,
    isUploading,
    error,
    fileInputRef,
    handleFileSelect,
    triggerFileInput,
    clearImage,
    resetError,
  };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}